/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Typography } from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';
import { selectServiceTierChangeRequestEmail } from '../../../store';
import appTheme from '../../common/theme/appTheme.json';

interface ServiceTierChangeRequestErrorDialogProps {
  open: boolean;
  onDialogCancel: () => void;
  formRef: any;
}

const ServiceTierChangeRequestErrorDialog = (props: ServiceTierChangeRequestErrorDialogProps) => {
  const { open, onDialogCancel, formRef } = props;
  const serviceTierChangeRequestEmail = useSelector(selectServiceTierChangeRequestEmail, shallowEqual);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onDialogCancel();
      }}
      sx={{
        '.MuiDialog-paper': { width: '648px', height: '292px', borderRadius: '12px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: appTheme.viasatFontFamilyRegular,
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          letterSpacing: '0.2px',
          padding: '24px',
        }}
      >
        Request not submitted
        <IconButton onClick={onDialogCancel} sx={{ padding: '0px' }}>
          <CloseRoundedIcon sx={{ color: appTheme.grayTextColor }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography sx={dialogBodyStyle}>There was an error submitting your service tier change request.</Typography>
        <Typography sx={dialogBodyStyle}>Please try to resubmit your request again.</Typography>
        <Typography sx={dialogBodyStyle}>
          If you still have issues submitting your request please{' '}
          <Link
            target="_blank"
            href={`mailto:${serviceTierChangeRequestEmail}`}
            underline="hover"
            component="a"
            sx={{
              color: appTheme.textLinkColor,
            }}
          >
            contact us
          </Link>
          .
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            onDialogCancel();
          }}
          sx={{
            height: '40px',
            width: '98px',
            borderRadius: '1000px',
            backgroundColor: '#202E39',
            color: '#ffffff',
            fontFamily: 'Source Sans Pro',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '24px',
            letterSpacing: 0,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: '#202E39',
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            // Resubmit form
            if (formRef.current) {
              formRef.current.handleSubmit();
            }
          }}
          sx={{
            height: '40px',
            width: '98px',
            borderRadius: '1000px',
            backgroundColor: '#18ECFC',
            border: 'none',
            color: '#202E39',
            fontFamily: 'Source Sans Pro',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '24px',
            letterSpacing: 0,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: '#18ECFC',
            },
          }}
        >
          Try again
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default ServiceTierChangeRequestErrorDialog;

const dialogBodyStyle = {
  fontFamily: 'Source Sans Pro',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#FFFFFF',
};
